import { useState, useMemo, useRef, useEffect } from "react";
import type { LLMModel, LLMProvider } from "@pick-three-ai/constants";
import { LLM_PROVIDERS, getModelsByProvider } from "@pick-three-ai/constants";
import { ProviderIcon } from "./ProviderIcon";
import { Search, Check, X, ChevronDown } from "lucide-react";

interface ModelPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (model: LLMModel) => void;
  currentSelection: LLMModel | null;
}

export function ModelPicker({
  isOpen,
  onClose,
  onSelect,
  currentSelection,
}: ModelPickerProps) {
  const [search, setSearch] = useState("");
  const [selectedProvider, setSelectedProvider] = useState<LLMProvider | null>(null);
  const [expandedProviders, setExpandedProviders] = useState<Set<LLMProvider>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  const modelsByProvider = useMemo(() => getModelsByProvider(), []);

  const filteredModels = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    const result: Partial<Record<LLMProvider, LLMModel[]>> = {};

    for (const [provider, models] of Object.entries(modelsByProvider)) {
      // If a provider filter is active, skip other providers
      if (selectedProvider && provider !== selectedProvider) continue;

      const providerInfo = LLM_PROVIDERS[provider as LLMProvider];
      
      // If there's a search term, filter by it
      if (searchLower) {
        const filtered = models.filter(
          (model) =>
            model.toLowerCase().includes(searchLower) ||
            providerInfo.name.toLowerCase().includes(searchLower)
        );
        if (filtered.length > 0) {
          result[provider as LLMProvider] = filtered;
        }
      } else {
        // No search term - include all models for this provider
        result[provider as LLMProvider] = models;
      }
    }

    return result as Record<LLMProvider, LLMModel[]>;
  }, [search, selectedProvider, modelsByProvider]);

  const providerOrder: LLMProvider[] = [
    "openai",
    "anthropic",
    "google",
    "meta",
    "mistral",
    "xai",
    "deepseek",
    "alibaba",
    "cohere",
    "cursor",
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Picker Container - Bottom sheet on mobile, centered modal on desktop */}
      <div
        ref={containerRef}
        className="relative w-full sm:max-w-md max-h-[85vh] sm:max-h-[min(500px,70vh)] flex flex-col glass-popover rounded-t-2xl sm:rounded-2xl shadow-xl animate-fade-in overflow-hidden"
      >
        {/* Header with Search */}
        <div className="flex-shrink-0 p-3 sm:p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search models or providers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-base sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
          
          {/* Horizontal Provider Filter */}
          <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-none pb-1">
            {providerOrder.map((provider) => {
              const providerInfo = LLM_PROVIDERS[provider];
              const isActive = selectedProvider === provider;
              return (
                <button
                  key={provider}
                  type="button"
                  onClick={() => setSelectedProvider(isActive ? null : provider)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors
                    ${isActive 
                      ? 'bg-accent text-white' 
                      : 'bg-white/10 text-muted-foreground hover:bg-white/15'
                    }`}
                >
                  <ProviderIcon provider={provider} size={14} />
                  {providerInfo.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Model List */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-2">
          {providerOrder.map((provider) => {
            const models = filteredModels[provider];
            if (!models || models.length === 0) return null;

            const providerInfo = LLM_PROVIDERS[provider];
            const isExpanded = expandedProviders.has(provider);
            const hasSearchFilter = search.trim().length > 0;
            const hasProviderFilter = selectedProvider !== null;
            
            // Show all models if: search is active, provider filter is active, provider is expanded, or 5 or fewer models
            const shouldShowAll = hasSearchFilter || hasProviderFilter || isExpanded || models.length <= 5;
            const visibleModels = shouldShowAll ? models : models.slice(0, 5);
            const hiddenCount = models.length - 5;

            return (
              <div key={provider} className="mb-3 last:mb-0">
                {/* Provider Header */}
                <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
                  <ProviderIcon
                    provider={provider}
                    size={16}
                    className="text-muted-foreground"
                  />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {providerInfo.name}
                  </span>
                </div>

                {/* Models */}
                <div className="space-y-0.5">
                  {visibleModels.map((model) => {
                    const isSelected = currentSelection === model;

                    return (
                      <button
                        key={model}
                        type="button"
                        onClick={() => {
                          setSearch("");
                          onSelect(model);
                        }}
                        className={`
                          w-full flex items-center justify-between gap-3
                          px-3 py-2.5 rounded-xl
                          text-left text-sm
                          transition-all duration-150
                          ${
                            isSelected
                              ? "bg-accent/20 text-foreground"
                              : "text-foreground hover:bg-white/5"
                          }
                        `}
                      >
                        <span className="font-medium">{model}</span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-accent flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                  
                  {/* Show other models button */}
                  {!shouldShowAll && hiddenCount > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedProviders(prev => {
                          const next = new Set(prev);
                          next.add(provider);
                          return next;
                        });
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left text-sm text-muted-foreground hover:bg-white/5 transition-colors"
                    >
                      <ChevronDown className="w-4 h-4" />
                      <span>Show {hiddenCount} other {providerInfo.name} models</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {Object.keys(filteredModels).length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <span className="text-muted-foreground text-sm">
                No models found
              </span>
              <button
                type="button"
                onClick={() => setSearch("")}
                className="mt-2 text-xs text-accent hover:underline"
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

